import { createContext, useContext, useEffect, useState } from "react";
import * as authService from "../services/authService";
import { getMe } from "../services/profileService";

const AuthCtx = createContext({
  user: null,
  profile: null,
  isOwner: false,
  loading: false,
  error: null,
  login: async () => {},
  register: async () => {},
  logout: () => {},
  refreshProfile: async () => null,
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => authService.getStoredUser());
  // `profile` = ProfileDto completo (inclui empresa, logoUrl etc). Consumido
  // pela sidebar pra branding white-label e pela PerfilPage pro form.
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Re-sincroniza com o storage caso outra aba mude o token.
  useEffect(() => {
    const onStorage = () => setUser(authService.getStoredUser());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Sempre que o user muda (login / reload com token válido), busca o profile.
  // Sem user → profile limpo.
  useEffect(() => {
    if (user) {
      refreshProfile();
    } else {
      setProfile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.guiaId]);

  async function refreshProfile() {
    try {
      const me = await getMe();
      setProfile(me);
      return me;
    } catch {
      // Se falhar (ex: 401 após token expirar), o interceptor em api.js já
      // redireciona pro login — aqui só limpamos o state local.
      setProfile(null);
      return null;
    }
  }

  async function login(email, senha) {
    setLoading(true);
    setError(null);
    try {
      const data = await authService.login(email, senha);
      setUser({
        guiaId: data.guiaId,
        nome: data.nome,
        email: data.email,
        expiresAt: data.expiresAt,
      });
      return true;
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        (err?.response?.status === 401
          ? "E-mail ou senha inválidos."
          : "Falha ao conectar com o servidor.");
      setError(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }

  // Espelho do login — mesma sequência: service POST → localStorage (interno
  // ao service) → setUser → effect dispara refreshProfile automaticamente.
  // Só muda o endpoint chamado e o payload. Após retornar true, o App.jsx
  // re-renderiza pra Dashboard porque `user` virou truthy.
  async function register(payload) {
    setLoading(true);
    setError(null);
    try {
      const data = await authService.register(payload);
      setUser({
        guiaId: data.guiaId,
        nome: data.nome,
        email: data.email,
        expiresAt: data.expiresAt,
      });
      return { ok: true };
    } catch (err) {
      const status = err?.response?.status;
      const body = err?.response?.data;
      let msg;
      if (status === 409) {
        // E-mail duplicado — mensagem amigável do servidor
        msg = body?.message || "Esse e-mail já tem uma conta.";
      } else if (status === 400) {
        // Erros do Identity vêm como { errors: [string[]] } ou { message }
        if (Array.isArray(body?.errors) && body.errors.length > 0) {
          msg = body.errors.join(" · ");
        } else {
          msg = body?.message || "Verifique os campos e tente de novo.";
        }
      } else if (status === 429) {
        msg = "Muitas tentativas de cadastro. Aguarde uma hora e tente de novo.";
      } else {
        msg = body?.message || "Falha ao conectar com o servidor.";
      }
      setError(msg);
      return { ok: false, error: msg, status };
    } finally {
      setLoading(false);
    }
  }

  function logout() {
    authService.logout();
    setUser(null);
    setProfile(null);
  }

  // Derivado de role: o ProfileDto (PR 1 backend) traz `roles: string[]`.
  // Frontend usa pra mostrar/esconder o menu admin e gatear a rota admin.
  const isOwner = profile?.roles?.includes("Owner") ?? false;

  return (
    <AuthCtx.Provider
      value={{ user, profile, isOwner, loading, error, login, register, logout, refreshProfile }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
